import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { galleryData } from '../data/mockData';

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [filter, setFilter] = useState('all');

  const categories = ['all', ...new Set(galleryData.map(item => item.category))];
  
  const filteredImages = filter === 'all' 
    ? galleryData 
    : galleryData.filter(item => item.category === filter);

  const openLightbox = (image) => {
    setSelectedImage(image);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const navigateImage = (direction) => {
    const currentIndex = filteredImages.findIndex(img => img.id === selectedImage.id);
    let newIndex;
    
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % filteredImages.length;
    } else {
      newIndex = (currentIndex - 1 + filteredImages.length) % filteredImages.length;
    }
    
    setSelectedImage(filteredImages[newIndex]);
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section" style={{ background: 'var(--warm-gradient)' }}>
        <div className="container text-center">
          <h1 className="mb-6 fade-in" style={{ color: 'var(--charcoal)' }}>
            Our Gallery
          </h1>
          <p className="text-xl max-w-3xl mx-auto fade-in-delay-1" style={{ color: 'var(--charcoal)' }}>
            Step inside our luxurious sanctuary and witness the artistry of our treatments. 
            Every detail is designed to create an atmosphere of elegance and tranquility.
          </p>
        </div>
      </section>

      {/* Filter Buttons */}
      <section className="py-8" style={{ backgroundColor: 'var(--pure-white)' }}>
        <div className="container">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setFilter(category)}
                className={`px-6 py-2 rounded-full transition-all duration-300 ${
                  filter === category
                    ? 'text-white shadow-lg' 
                    : 'bg-white text-charcoal border-2 hover:bg-warm-beige'
                }`}
                style={{
                  backgroundColor: filter === category ? 'var(--champagne-gold)' : 'var(--pure-white)',
                  borderColor: 'var(--champagne-gold)',
                  color: filter === category ? 'var(--pure-white)' : 'var(--charcoal)'
                }}
              >
                {category === 'all' ? 'All Photos' : category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="section">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredImages.map((image, index) => (
              <div 
                key={image.id}
                className={`group cursor-pointer overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all duration-500 fade-in-delay-${(index % 4) + 1}`}
                onClick={() => openLightbox(image)}
              >
                <div className="relative aspect-square overflow-hidden">
                  <img 
                    src={image.image}
                    alt={image.alt}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full">
                        <span className="text-sm font-medium" style={{ color: 'var(--charcoal)' }}>
                          View Image
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 bg-white">
                  <div className="text-sm font-medium mb-1" style={{ color: 'var(--champagne-gold)' }}>
                    {image.category}
                  </div>
                  <div className="text-sm text-gray-600">
                    {image.alt}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="relative max-w-7xl max-h-full">
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-colors"
            >
              <X className="w-6 h-6 text-white" />
            </button>

            <button
              onClick={() => navigateImage('prev')}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 p-2 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-colors"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>

            <button
              onClick={() => navigateImage('next')}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 p-2 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-colors"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>

            <img
              src={selectedImage.image}
              alt={selectedImage.alt}
              className="max-w-full max-h-full object-contain rounded-lg"
            />

            <div className="absolute bottom-4 left-4 right-4 text-center">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <div className="text-white font-medium mb-1">
                  {selectedImage.category}
                </div>
                <div className="text-white/80 text-sm">
                  {selectedImage.alt}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="section" style={{ background: 'var(--blush-gradient)' }}>
        <div className="container text-center">
          <h2 className="mb-6 fade-in" style={{ color: 'var(--charcoal)' }}>
            Experience It Yourself
          </h2>
          <p className="text-xl max-w-2xl mx-auto mb-8 fade-in-delay-1" style={{ color: 'var(--charcoal)' }}>
            Ready to step into our world of luxury and transformation? 
            Book your appointment and become part of our gallery of beautiful experiences.
          </p>
          <button 
            className="btn-primary fade-in-delay-2"
            onClick={() => window.location.href = '/contact'}
          >
            Book Your Visit
          </button>
        </div>
      </section>
    </div>
  );
};

export default Gallery;